/**
 * Idle / input-activity tracker — app-wide singleton.
 *
 * Listens for real DOM input on the window and exposes the user's
 * last-activity timestamp (epoch-ms UTC). The WebSocketProvider attaches
 * the value to every heartbeat so the server's idle-sweep can fire
 * auto-clock-out (15m) and force-logout (60m) thresholds.
 *
 * Independent of the existing "presence" axis (WS reachability) and the
 * "clocked-in" axis (work intent). The single concern here is whether
 * the user's browser is receiving real input.
 *
 * Side-effect: while the user is active, periodically rotates the JWT via
 * the existing `refreshToken()` helper so an active rep never sees a mid-
 * work auth interruption regardless of Keycloak token / SSO-idle settings.
 *
 * On-call signal: a rep on a Twilio call is engaged with the phone but may
 * not be touching mouse/keyboard. The softphone fires an activity-tick
 * every 60s while a call is connected — both as a window CustomEvent (for
 * the same tab) and on BroadcastChannel('apex-call') (for sibling tabs
 * whose `lastActivityAt` would otherwise drift stale and overwrite the
 * leader tab's fresh value via the heartbeat HSET). Each tab calls
 * `markActive()` when it receives the tick.
 *
 * Lifecycle:
 *   - init() once after auth lands. Idempotent — a second call is a no-op.
 *   - dispose() removes window listeners. Used by tests; in the live app
 *     the singleton survives until tab close.
 *
 * Time discipline: all timestamps in this module are `Date.now()` epoch-ms
 * (UTC). Never an ISO string. Never a Date object. Never a tz-aware value.
 */

import { refreshToken } from './auth-fetch';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Events that count as user input. Tab visibility alone does NOT count. */
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'click',
  'keydown',
  'scroll',
  'touchstart',
  'pointerdown',
  'focus',
] as const;

/** Ignore mousemove events firing closer together than this. Other events
 *  fire too rarely to need throttling. Picked to keep the activity-driven
 *  refresh check from running on every pixel of cursor motion. */
const MOUSEMOVE_THROTTLE_MS = 1_000;

/** Minimum time between activity-driven `refreshToken()` calls. Conservative
 *  enough that an active rep never trips the realm's SSO-idle window
 *  regardless of token lifespan settings. The reactive 401-retry path in
 *  apiFetch is the safety net; this is the proactive layer that prevents
 *  the user from ever hitting a 401 in the first place. */
const REFRESH_COOLDOWN_MS = 4 * 60 * 1_000;

/** BroadcastChannel name + message type for the on-call activity tick. */
const CALL_CHANNEL_NAME = 'apex-call';
const ACTIVITY_TICK_MSG = 'activity-tick';
/** Window event name dispatched by the softphone in the same tab. */
const ACTIVITY_TICK_EVENT = 'apex:activity-tick';

// ---------------------------------------------------------------------------
// Module state (singleton)
// ---------------------------------------------------------------------------

let lastActivityAt = Date.now();
let lastMouseMoveAt = 0;
let lastRefreshAt = Date.now();
let cleanup: (() => void) | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Latest user-input timestamp (epoch-ms UTC). */
export function getLastActivityAt(): number {
  return lastActivityAt;
}

/**
 * Bump `lastActivityAt` to now and trigger a JWT refresh if the cooldown
 * has elapsed. Public so the softphone can flag on-call activity without
 * a synthetic DOM event, and so the apex-call BroadcastChannel listener
 * can apply the same effect on sibling tabs.
 */
export function markActive(): void {
  lastActivityAt = Date.now();
  maybeRefresh();
}

/**
 * Wire window listeners. Idempotent — calling more than once is a no-op.
 * Returns the dispose function (also stored module-locally for tests).
 */
export function init(): () => void {
  if (cleanup) return cleanup;

  // Initialize fresh — the module may have been imported well before init
  // runs (auth-gated mount).
  const now = Date.now();
  lastActivityAt = now;
  lastRefreshAt = now;
  lastMouseMoveAt = 0;

  const handler = (ev: Event): void => {
    if (ev.type === 'mousemove') {
      const t = Date.now();
      if (t - lastMouseMoveAt < MOUSEMOVE_THROTTLE_MS) return;
      lastMouseMoveAt = t;
      lastActivityAt = t;
    } else {
      lastActivityAt = Date.now();
    }
    maybeRefresh();
  };

  for (const evt of ACTIVITY_EVENTS) {
    window.addEventListener(evt, handler, { passive: true });
  }

  // Same-tab tick — softphone dispatches this in the leader tab where the
  // setInterval lives. BroadcastChannel does not echo to its sender.
  const tickHandler = (): void => markActive();
  window.addEventListener(ACTIVITY_TICK_EVENT, tickHandler);

  // Cross-tab tick — sibling tabs whose own `lastActivityAt` is stale would
  // otherwise overwrite the leader's fresh value via the heartbeat HSET.
  const channel =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CALL_CHANNEL_NAME) : null;
  const channelHandler = (e: MessageEvent): void => {
    const data = e.data as { type?: unknown } | null;
    if (data && data.type === ACTIVITY_TICK_MSG) markActive();
  };
  channel?.addEventListener('message', channelHandler);

  cleanup = () => {
    for (const evt of ACTIVITY_EVENTS) {
      window.removeEventListener(evt, handler);
    }
    window.removeEventListener(ACTIVITY_TICK_EVENT, tickHandler);
    channel?.removeEventListener('message', channelHandler);
    channel?.close();
    cleanup = null;
  };

  return cleanup;
}

/** Tear down listeners. Used by tests; the live app keeps the singleton. */
export function dispose(): void {
  cleanup?.();
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/**
 * Activity-driven refresh — keeps the JWT fresh for an active user without
 * waiting for a 401. Existing dedupe in `refreshToken()` means overlapping
 * calls coalesce to one in-flight request.
 */
function maybeRefresh(): void {
  if (lastActivityAt - lastRefreshAt >= REFRESH_COOLDOWN_MS) {
    lastRefreshAt = lastActivityAt;
    void refreshToken();
  }
}

// ---------------------------------------------------------------------------
// Test hooks — exported only so unit tests can reset module state between
// cases. Production callers should never touch these.
// ---------------------------------------------------------------------------

export function _resetForTests(): void {
  cleanup?.();
  const now = Date.now();
  lastActivityAt = now;
  lastMouseMoveAt = 0;
  lastRefreshAt = now;
}
